import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../utils/authService';

const HotelResults = ({ 
  hotelResults, 
  totalHotels, 
  apiStatus, 
  searchLoading, 
  searchProgress, 
  hasMoreHotels, 
  onLoadMore,
  onClearFilters,
  searchId,
  searchForm // Add searchForm to get room allocation data
}) => {
  
  const navigate = useNavigate();
  const loadMoreRef = useRef(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Infinite scroll - automatically load more when user scrolls near bottom
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMoreHotels && !isLoadingMore && !searchLoading) {
          handleLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '200px', // Start loading 200px before reaching the element
        threshold: 0.1
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasMoreHotels, isLoadingMore, searchLoading]);

  // Show scroll to top button after 150 hotels
  useEffect(() => {
    const handleScroll = () => {
      // Show button if more than 150 hotels and scrolled down
      if (totalHotels >= 150 && window.scrollY > 500) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [totalHotels]);

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMoreHotels) return;
    
    setIsLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setIsLoadingMore(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const createSafeId = (name) => {
    return name.replace(/\s+/g, '-');
  };

  // Function to create roomAllocations parameter from search form
  const createRoomAllocations = () => {
    if (!searchForm || !searchForm.rooms) {
      return '';
    }

    try {
      // Create room allocation array matching the backend RoomAllocationDto format
      const roomAllocations = searchForm.rooms.map((room, index) => ({
        roomNumber: index + 1,
        adults: room.adults,
        children: room.children,
        childAges: room.childAges || []
      }));

      // Convert to JSON and encode as Base64 (URL-safe)
      const json = JSON.stringify(roomAllocations);
      const encoded = btoa(unescape(encodeURIComponent(json)));
      return encoded;
    } catch (error) {
      console.error('Error creating room allocations:', error);
      return '';
    }
  };

  const handleBookNow = (hotel) => {
    if (!hotel.hotelId || !hotel.providerName || !searchId) {
      console.error('Missing required parameters for hotel booking:', { 
        hotelId: hotel.hotelId, 
        provider: hotel.providerName, 
        searchId 
      });
      return;
    }

    // Build URL for hotel details page
    const params = new URLSearchParams({
      provider: hotel.providerName,
      searchId: searchId,
      // Add additional parameters that might be needed
      checkInDate: hotel.checkInHistory || searchForm?.checkInDate || '',
      checkOutDate: hotel.checkOutHistory || searchForm?.checkOutDate || '',
      destination: hotel.citySearchHistory || searchForm?.destination || '',
      roomAllocations: createRoomAllocations() // Add room allocations parameter
    });

    // Get current user role and determine URL prefix
    const user = authService.getUser();
    const rawRole = user?.role || 'ROLE_AGENT';
    const role = rawRole.toUpperCase();
    
    let pathSegment = 'agent';
    if (role === 'ROLE_ADMIN' || role === 'ADMIN') {
      pathSegment = 'admin';
    } else if (role === 'ROLE_SUPPORT' || role === 'SUPPORT') {
      pathSegment = 'support';
    }

    const url = `/${pathSegment}/dashboard/searchhotel/details/${hotel.hotelId}?${params.toString()}`;
    
    // Open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderHotelStars = (hotelStar) => {
    if (!hotelStar || hotelStar < 1 || hotelStar > 5) {
      return null;
    }

    const filledStars = Math.floor(hotelStar);
    const emptyStars = hotelStar !== 5 && hotelStar >= 1 && hotelStar < 5 ? (5 - filledStars) : 0;

    return (
      <div className="flex items-center" style={{ fontSize: '14px', lineHeight: '14px' }}>
        {Array.from({ length: filledStars }, (_, i) => (
          <span 
            key={`filled-${i}`} 
            className="filled-star" 
            style={{ color: 'gold', marginRight: '2px' }}
          >
            ★
          </span>
        ))}
        {Array.from({ length: emptyStars }, (_, i) => (
          <span 
            key={`empty-${i}`} 
            className="empty-star" 
            style={{ color: 'lightgray', marginRight: '2px' }}
          >
            ☆
          </span>
        ))}
      </div>
    );
  };

  // Helper to deduplicate vendor options within a hotel group
  const getUniqueHotels = (hotelsList) => {
    if (!Array.isArray(hotelsList)) return [];
    const seen = new Set();
    return hotelsList.filter(hotel => {
      const provider = (hotel.providerName || '').trim().toLowerCase();
      const fare = hotel.rooms?.totalFare || hotel.rooms?.netFare || '';
      const code = hotel.hotelId || hotel.hotelCode || hotel.hotelName || '';
      const roomType = hotel.rooms?.roomTypeName || hotel.rooms?.roomCategory?.[0] || '';
      
      const key = `${provider}_${fare}_${code}_${roomType}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  return (
    <div className="flex-1">
      {/* Hotel Results */}
      <div className="space-y-4">
        {Object.keys(hotelResults).length > 0 ? (
          Object.entries(hotelResults).map(([hotelName, rawHotels]) => {
            const hotels = getUniqueHotels(rawHotels);
            if (!hotels || hotels.length === 0) return null;
            return (
            <div key={hotelName} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
              {/* Hotel Header */}
              <div 
                className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 cursor-pointer hover:from-blue-50 hover:to-indigo-50 transition-all duration-200"
                onClick={() => {
                  const cleanName = createSafeId(hotelName);
                  const content = document.getElementById(`accordion-${cleanName}`);
                  const icon = document.getElementById(`icon-${cleanName}`);
                  if (content && icon) {
                    content.classList.toggle('hidden');
                    icon.classList.toggle('rotate-180');
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">{hotelName}</h3>
                      <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full border border-blue-200">
                        {hotels.length} option{hotels.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    {/* Provider Pills */}
                    <div className="flex flex-wrap gap-2">
                      {hotels.slice(0, 3).map((hotel, index) => (
                        <div 
                          key={index}
                          className={`px-3 py-1 rounded-full text-sm font-medium text-white shadow-sm ${
                            hotel.providerName?.toLowerCase() === 'tripjack' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                            hotel.providerName?.toLowerCase() === 'tbo' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                            hotel.providerName?.toLowerCase() === 'booking' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                            hotel.providerName?.toLowerCase() === 'agoda' ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                            'bg-gradient-to-r from-gray-500 to-gray-600'
                          }`}
                        >
                          {hotel.providerName?.toUpperCase()} ₹{hotel.rooms?.totalFare?.toLocaleString() || 'N/A'}
                        </div>
                      ))}
                      {hotels.length > 3 && (
                        <div className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm font-medium">
                          +{hotels.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-xs text-gray-500 mb-1"></div>
                      <div className="text-2xl font-bold text-green-600">
                        ₹{hotels[0]?.rooms?.totalFare?.toLocaleString() || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500"></div>
                    </div>
                    <div className="p-2 bg-white rounded-full shadow-sm">
                      <svg 
                        id={`icon-${createSafeId(hotelName)}`}
                        className="w-5 h-5 text-gray-400 transition-transform duration-200"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hotel Details */}
              <div id={`accordion-${createSafeId(hotelName)}`} className="hidden">
                {hotels.map((hotel, index) => (
                  <div key={index} className="border-b border-gray-100 last:border-b-0">
                    <div className="p-4 hover:bg-gray-50 transition-colors duration-200">
                      <div className="flex items-start space-x-4">
                        {/* Hotel Image */}
                        <div className="flex-shrink-0">
                          <div className="w-20 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg overflow-hidden shadow-sm">
                            {hotel.hotelImageUrl ? (
                              <img 
                                src={hotel.hotelImageUrl} 
                                alt={hotel.hotelName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Hotel Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">{hotel.hotelName}</h4>
                          
                          {/* Star Rating */}
                          <div className="flex items-center space-x-2 mb-2">
                            {renderHotelStars(hotel.hotelStar)}
                          </div>
                          
                          {/* Room Category - Display first room category */}
                          {hotel.rooms?.roomCategory && hotel.rooms.roomCategory.length > 0 && (
                            <div className="mb-2">
                              <span className="bg-indigo-50 text-indigo-700 text-sm px-3 py-1 rounded-full border border-indigo-200 font-medium">
                                {hotel.rooms.roomCategory[0]}
                              </span>
                            </div>
                          )}
                          
                          {/* Cancellation Policy */}
                          {hotel.isRefundable && (
                            <div className="mb-2">
                              <span className="bg-green-50 text-green-700 text-sm px-3 py-1 rounded-full border border-green-200 font-medium flex items-center w-fit">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Cancellation Available
                              </span>
                            </div>
                          )}
                          
                          {hotel.hotelAddress && (
                            <div className="text-sm text-gray-600 flex items-center">
                              <svg className="w-4 h-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              {hotel.hotelAddress}
                            </div>
                          )}
                        </div>

                        {/* Price and Booking */}
                        <div className="flex-shrink-0 text-right">
                          <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium text-white mb-3 shadow-sm ${
                            hotel.providerName?.toLowerCase() === 'tripjack' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                            hotel.providerName?.toLowerCase() === 'tbo' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                            hotel.providerName?.toLowerCase() === 'booking' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                            hotel.providerName?.toLowerCase() === 'agoda' ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                            'bg-gradient-to-r from-gray-500 to-gray-600'
                          }`}>
                            {hotel.providerName?.toUpperCase()}
                          </div>
                          
                          <div className="mb-3">
                            <div className="text-2xl font-bold text-green-600">
                              ₹{hotel.rooms?.totalFare?.toLocaleString() || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500"></div>
                          </div>
                          
                          <button 
                            onClick={() => handleBookNow(hotel)}
                            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                          >
                            <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            Book Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
        ) : !searchLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Hotels Found</h3>
              <p className="text-gray-600 mb-6">
                We couldn't find any hotels matching your search criteria. Try adjusting your filters or search parameters.
              </p>
              <div className="flex justify-center space-x-3">
                <button 
                  onClick={onClearFilters}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Clear Filters
                </button>
                <button 
                  onClick={() => window.location.reload()}
                  className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  New Search
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Infinite Scroll Trigger & Load More Section */}
      {hasMoreHotels && (
        <div ref={loadMoreRef} className="text-center mt-6">
          {isLoadingMore ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200"></div>
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
              </div>
              <p className="mt-4 text-gray-600 font-medium">Loading more hotels...</p>
            </div>
          ) : (
            <button
              onClick={handleLoadMore}
              className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white text-sm font-medium px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Load More Hotels
            </button>
          )}
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 z-50"
          aria-label="Scroll to top"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default HotelResults;